/* eslint-disable max-len */
import React from 'react';
import _ from 'lodash';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import appConfig from 'app-config';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import { invalidSsnList } from './MLInvalidSsnList';

function MLGafUtil() {
    const viewModelService = React.useContext(ViewModelServiceContext);    

    const isQnB = appConfig.portalCode === 'QB';

    const gafEligibilityStatusTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('GAFEligibilityStatus_ML');
    const ELIG_STATUS = {
        NOT_ELIGIBLE: gafEligibilityStatusTypelist.getCode('not_eligible'),
        ELIGIBLE_EFILE_VER_REQUIRED: gafEligibilityStatusTypelist.getCode('eligible_efile_ver_required'),
        ELIGIBLE_NO_EFILE_VER_REQUIRED: gafEligibilityStatusTypelist.getCode('eligible_no_efile_ver_required')
    };

    /* Delete GAF info when leaving GAF accordion if any of these scenarios are verified:
        1: User not eligible for PD nor DB
        2: User not eligible for PD, eligible for DB (verification required) but not verified
        4: User not eligible for DB, eligible for PD (verification required) but not verified
        5: User eligible with verification required for both PD and DB but not verified
    */
    const shouldDeleteGafInfo = (info) => {
        if (!info) {
            return false;
        }

        const isEligibilityNotVerified = !info.eligibilityVerified.value;

        if (info.eligibleForPayrollDeduct.value === ELIG_STATUS.NOT_ELIGIBLE && info.eligibleForDirectBill.value === ELIG_STATUS.NOT_ELIGIBLE) {
            return true;
        }

        if (isEligibilityNotVerified
            && info.eligibleForPayrollDeduct.value !== ELIG_STATUS.ELIGIBLE_NO_EFILE_VER_REQUIRED
            && info.eligibleForDirectBill.value === ELIG_STATUS.ELIGIBLE_EFILE_VER_REQUIRED) {
            return true;
        }

        if (isEligibilityNotVerified
            && info.eligibleForPayrollDeduct.value === ELIG_STATUS.ELIGIBLE_EFILE_VER_REQUIRED
            && info.eligibleForDirectBill.value !== ELIG_STATUS.ELIGIBLE_NO_EFILE_VER_REQUIRED) {
            return true;
        }

        return false;
    };

    const isEFileVerificationNeeded = (model) => model.eligibleForPayrollDeduct.value === ELIG_STATUS.ELIGIBLE_EFILE_VER_REQUIRED || model.eligibleForDirectBill.value === ELIG_STATUS.ELIGIBLE_EFILE_VER_REQUIRED;

    const validatePayrollDeductEligibility = (model, submissionVM, qnbMode) => {
        const isPayrollDeductAvailable = model.payrollDeductAvailable.value;
        const isEligibilityVerified = model.eligibilityVerified.value;
        const eligibilityModel = model.eligibleForPayrollDeduct.value;

        if(qnbMode) {
            return isPayrollDeductAvailable && (eligibilityModel.code === ELIG_STATUS.ELIGIBLE_EFILE_VER_REQUIRED.code && isEligibilityVerified) && !_.isEmpty(_.get(submissionVM, 'baseData.employeeInfo_ML.value.name'));
        }

        return isPayrollDeductAvailable && 
            (
                eligibilityModel.code === ELIG_STATUS.ELIGIBLE_NO_EFILE_VER_REQUIRED.code || 
                (eligibilityModel.code === ELIG_STATUS.ELIGIBLE_EFILE_VER_REQUIRED.code && isEligibilityVerified)
            )
    };

    const validateGroupDiscountEligibility = (model) => model.eligibleForDirectBill.value === ELIG_STATUS.ELIGIBLE_NO_EFILE_VER_REQUIRED
        || (model.eligibleForDirectBill.value === ELIG_STATUS.ELIGIBLE_EFILE_VER_REQUIRED && model.eligibilityVerified.value);

    const validateEmployeeNumberVisibility = (model, employeeInfo) => {
        const isEmpDepEmpty = _.isUndefined(model.employeeDepartmentNumberLength.value);
        const isEmpMinZero = model?.employeeNumberMinLength?.value === 0;
        const isEmpMinmaxZero = model?.employeeNumberMinLength?.value === 0 && model?.employeeNumberMaxLength?.value === 0;
        const customerEligibilityFileIndicator = model?.customerEligibilityFileIndicator?.value;
        const employeeNumberData = (!_.isEmpty(employeeInfo?.employeeNumber) && !_.isEmpty(employeeInfo?.name));

        return isEmpDepEmpty && !(isEmpMinZero || isEmpMinmaxZero) && (customerEligibilityFileIndicator!==true || employeeNumberData)
    };

    const validateDepartmentNumberVisibility = (model, employeeInfo) => {
        const isEmpDepEmpty = _.isUndefined(model.employeeDepartmentNumberLength.value);
        const isDepMinZero = model.departmentNumberMinLength.value === 0;
        const isDepMinmaxZero = model.departmentNumberMinLength.value === 0 && model.departmentNumberMaxLength.value === 0;
        const customerEligibilityFileIndicator = model?.customerEligibilityFileIndicator?.value;
        const departmentNumberData = (!_.isEmpty(employeeInfo?.departmentNumber) && !_.isEmpty(employeeInfo?.name));

        return isEmpDepEmpty && !(isDepMinmaxZero || isDepMinZero) && (customerEligibilityFileIndicator!==true || departmentNumberData)
    };


       const isTransferOn = (model, isEndorsement) => (
            isEndorsement
            && model?.paymentDetails_ML?.selectedPaymentPlan?.value === appConfig.mlPaymentPlan.MLPayrollDeductPlanPublicId
            && !_.isEqual(model?.baseData?.employeeInfo_ML?.isBasedOnPayroll?.value, true)
    )
       
    const isTransferOff = (model, isEndorsement) => (
            isEndorsement
            && model?.paymentDetails_ML?.selectedPaymentPlan?.value !== appConfig.mlPaymentPlan.MLPayrollDeductPlanPublicId
            && _.isEqual(model?.baseData?.employeeInfo_ML?.isBasedOnPayroll?.value, true)
        )
 
    const isEditableVisibleForPolicyChange = (model, isEndorsement) => {
        if(isEndorsement){
            if(isTransferOff(model, isEndorsement) || (!isTransferOn(model, isEndorsement)
             && model?.paymentDetails_ML?.selectedPaymentPlan?.value === appConfig.mlPaymentPlan.MLPayrollDeductPlanPublicId)){
                 return false
            } else {
                return true
            }
        }
 
        return false
    }

    const validateEmployeeDepartmentNumberVisibility = (
      model,
      employeeInfo,
      isPaymentScreen,
      isEndorsement,
      isPaymentMethodPayrollDeduct
    ) => {
      const customerEligibilityFileIndicator =
        model?.customerEligibilityFileIndicator?.value;

      return (
        !_.isUndefined(model.employeeDepartmentNumberLength.value) &&
        ((
            (!customerEligibilityFileIndicator || customerEligibilityFileIndicator && !_.isEmpty(employeeInfo?.ssn))
         && !(isPaymentScreen))
         ||

          (isPaymentMethodPayrollDeduct &&
            ((isEFileVerificationNeeded(model) &&
              customerEligibilityFileIndicator &&
              !_.isEmpty(employeeInfo?.ssn)) ||
              (!isEFileVerificationNeeded(model) &&
                (isPaymentScreen || isEndorsement)))))
      );
    };

    const validateAlternateIdVisibility = (model, employeeInfo, isPaymentScreen, isEndorsement, isPaymentMethodPayrollDeduct) => {
        const isAltUsageInd = model.alternateIdUsage.value;
        const isEmpDepLengthEmpty = _.isUndefined(model.employeeDepartmentNumberLength.value);

        return (isAltUsageInd && isEFileVerificationNeeded(model) && isEmpDepLengthEmpty)
            && (!_.isEmpty(employeeInfo?.ssn) 
            || ((isPaymentScreen || isEndorsement) && isPaymentMethodPayrollDeduct))
    };

    const normalizeAddr = (addr) => {
        let addStr = String(addr)
                        .toLowerCase()
                        .replace(/[^a-z0-9 ]/g, " ") //replace punctuation
                        .replace(/\s+/g," ") //replace extra spaces
                        .trim(); //remove leading or trailing spaces

        const addressDirectives = {
            street: "st", st: "st",
            avenue: "ave", ave: "ave",
            road: "rd", rd: "rd",
            boluevard: "blvd", blvd: "blvd",
            drive: "dr", dr: "dr",
            lane: "ln", ln: "ln",
            court: "ct", ct: "ct",
            north: "n", n: "n",
            south: "s", s: "s",
            east: "e", e: "e",
            west: "w", w: "w"
        };

        return addStr.split(" ").map(a => addressDirectives[a] ?? a).join(" ");
    };

    const isAddrMatching = (polAddr, eligAddr) => {
        const polAddrNormalized = normalizeAddr(polAddr);
        const eligAddrNormalized = normalizeAddr(eligAddr);

        return polAddrNormalized?.includes(eligAddrNormalized) || eligAddrNormalized?.includes(polAddrNormalized);
    };

    const isFirstNameMatching = (pniName, eligName) => {
        return pniName?.includes(eligName) || eligName?.includes(pniName)
    };

    const filterEligiblity = (eligInfos, pni) => {
        if(eligInfos.length == 1) {
            return eligInfos[0];
        } else {
            const firstNameFilter = eligInfos?.filter(res => isFirstNameMatching(pni?.firstName?.toLowerCase(),res?.name?.toLowerCase()));

            if(firstNameFilter?.length === 0) {
                return null;
            } 
            else if(firstNameFilter?.length === 1) {
                return firstNameFilter[0];
            } else {
                //Multiple first name match, filter by address
                const addressFilter = firstNameFilter?.filter(res => isAddrMatching(pni?.primaryAddress?.addressLine1,res?.address));

                if(addressFilter) {
                    return addressFilter[0];
                } else {
                    return null;
                }
            }
        }
    };


    const isNonServicingState = (model) => {
        const states = ["ME", "WV", "VT", "NH"]; 
        const polState = _.get(model, 'baseData.policyAddress.state.value.code');
        const productCode = _.get(model, 'baseData.productCode.value', '');

        if(states.includes(polState)) {
            return true;
        } else if(polState === "MS" && MLProductCodeUtil.isHomeOwners(productCode)){
            return true;
        }

        return false; 
    };

    const isQnBEmployerNameDataValid = (employerNameData) => {
        const defaultEmployeeName = '0000'
        if (!isQnB) {
            return true;
        }
        return employerNameData !== defaultEmployeeName;
    };

    const validateSsnField = (gafInfoObject, enableInvalidSSNValidation = false) => {
        const altIdLabel = gafInfoObject.gafInfo.altIdLabel.value;
        const ssn = _.get(gafInfoObject.gafInfo, 'ssn', undefined);
        const isEditMode =  _.get(gafInfoObject.gafInfo, 'isEditMode.value', false)
        const isEditPayrollDeduct =  _.get(gafInfoObject.gafInfo, 'isEditPayrollDeduct.value', false)
        const pattern = /^0+$/

        if ((gafInfoObject.gafInfo.alternateIdUsage.value === true) && (ssn.value !== '') && (ssn.value === gafInfoObject.gafInfo.alternateId.value)) {
            return [`Update required! SSN value must be different from ${altIdLabel}`]
        }

        if (
            (isEditMode || isEditPayrollDeduct) &&
            (pattern.test(ssn?.value) || 
            (invalidSsnList.includes(ssn?.value) && enableInvalidSSNValidation))) {
            return ["An invalid SSN has been entered. The SSN must be 9 numeric digits. Ex. 123456789"]
        }

        return ssn?.aspects?.validationMessages;

    };

    return {
        isEFileVerificationNeeded,
        shouldDeleteGafInfo,
        validatePayrollDeductEligibility,
        validateGroupDiscountEligibility,
        validateEmployeeNumberVisibility,
        validateDepartmentNumberVisibility,
        ELIG_STATUS,
        isEditableVisibleForPolicyChange,
        validateEmployeeDepartmentNumberVisibility,
        validateAlternateIdVisibility,
        filterEligiblity,
        isNonServicingState,
        isQnBEmployerNameDataValid,
        validateSsnField,
    };
}

export default MLGafUtil;
